import React, { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router-dom';
import {
  ClerkLoaded,
  ClerkLoading,
  AuthenticateWithRedirectCallback
} from '@clerk/clerk-react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from './components/web/Navbar';
import AuthGuard from '@/components/auth/AuthGuard';
import OneSignalConfig from '@/components/oneSIgnalConfig';
import ContactUs from './components/ContactUs';
import ImModal from '@/components/ImModal';
import LoadingLottie from '@/components/Loading';
import { initCustomEvent, Tracker } from './utils';
import {
  isMobile,
  getKeyFromUrlSearch,
  setStoredReferralCodeIfEmpty
} from '@/biz/tool';
import EmailUnsubscribe from './pages/EmailUnsubscribe';
import EmailConfirmed from './pages/EmailConfirmed';
import { useInvitesStore } from '@/hooks/useInvitesStore';

// 导入样式
import './index.css';

// 懒加载页面组件
const Home = React.lazy(
  () =>
    import(/* webpackChunkName: "page~components~Home" */ './components/Home')
);
const ClerkCallbackPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~clerkCallbackPage" */ './pages/login/clerkCallbackPage'
    )
);

/**
 * /clerk 路由包装：必须先由 AuthenticateWithRedirectCallback 处理 OAuth 回调并建立 session，
 * 否则 Google 等授权回调后 session 未建立，会变成未登录并跳到 /login。
 */
const ClerkCallbackRoute: React.FC = () => {
  const { pathname, search } = useLocation();
  const fallbackUrl = pathname + (search || '');
  return (
    <>
      <AuthenticateWithRedirectCallback
        signInFallbackRedirectUrl={fallbackUrl}
        signUpFallbackRedirectUrl={fallbackUrl}
      />
      <ClerkCallbackPage />
    </>
  );
};
const LoginPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~LoginPage" */ './pages/login/LoginPage'
    )
);
const SignupPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~SignupPage" */ './pages/login/SignupPage'
    )
);
const ForgotPasswordPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~ForgotPasswordPage" */ '@/pages/login/ForgotPasswordPage'
    )
);
const PlatformCallbackPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~PlatformCallbackPage" */ './pages/login/PlatformCallbackPage'
    )
);
const PlatformCallbackSassPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~PlatformCallbackSassPage" */ './pages/login/PlatformCallbackSassPage'
    )
);
const ConnectError = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~connectError" */ './pages/campaign/connectError'
    )
);
const ConnectSuccess = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~connectSuccess" */ './pages/campaign/connectSuccess'
    )
);
const ErrorPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~error" */ './pages/campaign/error'
    )
);
const MatchPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~match" */ './pages/campaign/match'
    )
);
const ConnectPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~connect" */ './pages/campaign/connect'
    )
);
const Wallet = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~wallet" */ './pages/campaign/wallet'
    )
);
const WalletPaymentMethod = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~walletPaymentMethod" */ './pages/campaign/walletPaymentMethod'
    )
);
const InvitesHome = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~invites" */ './pages/campaign/invites'
    )
);
const DetailNew = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~detailNew" */ './pages/campaign/detailNew'
    )
);
const DealsHome = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~deals" */ './pages/campaign/deals'
    )
);
const ContactInfo = React.lazy(
  () => import(/* webpackChunkName: "page~contactInfo" */ './pages/contactInfo')
);
const Settings = React.lazy(
  () => import(/* webpackChunkName: "page~settings" */ './pages/settings')
);
const IconsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~settings~icons" */ './pages/settings/icons'
    )
);

const Reviews = React.lazy(
  () => import(/* webpackChunkName: "page~settings~icons" */ './pages/Reviews')
);
// referral
const ReferralHome = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~referral~ReferralHome" */ './pages/Referral'
    )
);
const DeletePage = React.lazy(
  () => import(/* webpackChunkName: "page~delete" */ './pages/delete')
);

const App: React.FC = () => {
  const router = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const hideNavbar =
    router.pathname.startsWith('/email-unsubscribe') ||
    router.pathname.startsWith('/email-confirmed') ||
    router.pathname.startsWith('/delete') ||
    router.pathname === '/login' ||
    router.pathname === '/signup' ||
    router.pathname === '/forgot-password';
  const { setInvitesId } = useInvitesStore();

  useEffect(() => {
    initCustomEvent();
  }, [router]);

  // 邀请码：仅首次生效，URL 携带 REFERRALCODE 时写入 storage，后续不同值不覆盖
  useEffect(() => {
    const code = getKeyFromUrlSearch('REFERRALCODE');
    if (code) setStoredReferralCodeIfEmpty(code);
  }, [router.search]);

  // 处理 DM 短链接重定向: 转换为完整的 campaign detail URL
  useEffect(() => {
    if (router.pathname === '/') {
      const query = new URLSearchParams(router.search);
      const igOrderId = query.get('ig');

      if (igOrderId) {
        // 对应 https://xxx.inc?ig=$orderId
        // 上报 DM 短链接访问事件
        try {
          Tracker.gtag('event', 'screen_view', {
            event_category: 'dm_short_link_invitation_visit',
            utm_source: 'ig',
            utm_medium: 'dm',
            utm_campaign: 'invitation_campaign',
            order_id: igOrderId,
            value: 1
          });
        } catch (error) {
          console.error(
            '❌ Error sending dm_short_link_invitation_visit event:',
            error
          );
        }
        // 将短链接转换为完整的 campaign detail URL，并带上 UTM 参数
        // 新增处理，支持进入聚合链接，根据 ID 长度
        const fullUrl =
          igOrderId.length > 20
            ? `/campaign/detail/${igOrderId}?utm_source=ig&utm_medium=dm&utm_campaign=invitation_campaign`
            : `/campaign/invites?id=${igOrderId}&utm_source=ig&utm_medium=dm&utm_campaign=invitation_campaign`;

        navigate(fullUrl, { replace: true });
      } else if (igOrderId === '') {
        // 对应邀约注册 https://xxx.inc?ig
        // 上报 DM 邀约注册
        try {
          Tracker.gtag('event', 'screen_view', {
            event_category: 'dm_short_link_signup_visit',
            utm_source: 'ig',
            utm_medium: 'dm'
          });
        } catch (error) {
          console.error(
            '❌ Error sending dm_short_link_signup_visit event:',
            error
          );
        }
      }
      // TODO(pengrui): 后续扩展其他平台
    }
  }, [router.pathname, router.search, navigate]);

  // 监听路由变化，当进入 /campaign/invites 页面时，获取并保存 id 参数
  useEffect(() => {
    if (router.pathname === '/campaign/invites') {
      const query = new URLSearchParams(router.search);
      const id = query.get('id');
      if (id) {
        setInvitesId(id);
      }
    }
  }, [router.pathname, router.search, setInvitesId]);

  return (
    <div id="app" className={isMobile() ? '' : 'webAppContainer'}>
      <ClerkLoaded>
        <AuthGuard>
          <>
            {!hideNavbar && <Navbar />}
            <OneSignalConfig />
            <Suspense
              fallback={
                <div className="flex flex-col justify-center items-center h-[calc(60vh)] w-full">
                  <LoadingLottie width={160} height={160} />
                  <div className="text-gray-500 text-sm">
                    {t('common.loading')}
                  </div>
                </div>
              }
            >
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route
                  path="/forgot-password"
                  element={<ForgotPasswordPage />}
                />
                <Route path="/delete" element={<DeletePage />} />
                <Route path="/" element={<Home />} />
                {/* <Route path="/web" element={<Web />} /> */}
                <Route path="/campaign/error" element={<ErrorPage />} />
                <Route path="/match" element={<MatchPage />} />
                <Route path="/connect" element={<ConnectPage />} />
                <Route path="/contactInfo" element={<ContactInfo />} />
                <Route
                  path="/auth/:platform/callback"
                  element={<PlatformCallbackPage />}
                />
                <Route
                  path="/auth/:platform/"
                  element={<PlatformCallbackSassPage />}
                />
                <Route path="/clerk" element={<ClerkCallbackRoute />} />
                <Route
                  path="/auth/:platform/connectError"
                  element={<ConnectError />}
                />
                <Route
                  path="/auth/:platform/success"
                  element={<ConnectSuccess />}
                />
                {/*  */}
                {/* 以下是的新页面 */}
                {/*  */}
                <Route
                  path="/campaign/detail/:orderId"
                  element={<DetailNew />}
                />
                <Route
                  path="/campaign/invites/detail/:orderId"
                  element={<DetailNew />}
                />
                <Route
                  path="/campaign/deals/detail/:orderId"
                  element={<DetailNew />}
                />
                <Route path="/campaign/wallet/:orderId?" element={<Wallet />} />
                <Route
                  path="/campaign/wallet/payment/method/update/:type"
                  element={<WalletPaymentMethod />}
                />
                <Route path="/campaign/invites" element={<InvitesHome />} />
                <Route path="/campaign/deals" element={<DealsHome />} />
                {/* <Route path="/campaign/share" element={<ShareHome />} /> */}
                {/*  */}
                <Route path="/settings" element={<Settings />} />
                {/* referral */}
                <Route path="/referral" element={<ReferralHome />} />
                {/* 评论 */}
                <Route path="/reviews" element={<Reviews />} />
                {process.env.REACT_APP_ENV !== 'production' && (
                  <Route path="/icons" element={<IconsPage />} />
                )}
                {/*  */}
                <Route path="*" element={<ErrorPage />} />
                <Route
                  path="/email-unsubscribe"
                  element={<EmailUnsubscribe />}
                />
                <Route path="/email-confirmed" element={<EmailConfirmed />} />
              </Routes>
            </Suspense>
            <ImModal />
          </>
        </AuthGuard>
      </ClerkLoaded>
      <ClerkLoading>
        <div className="flex flex-col justify-center items-center h-[calc(60vh)] w-full">
          <LoadingLottie width={160} height={160} />
          <div className="text-gray-500 text-sm">{t('common.loading')}</div>
        </div>
      </ClerkLoading>
      <ContactUs />
    </div>
  );
};

export default App;
